// Import Packages
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Checkboxes } from '..';

// Import Material UI
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Close from '@material-ui/icons/Close';
import { TextInput } from '../TextInput/NewTextInput';

// Import Pages
import FieldLabel from '../FieldLabel/FieldLabel';
import ConnectorField from '../../../home/pages/automs/newFlow/middleArea/connectorField/ConnectorField';

// Import Recoil
import { triggerConf } from '../../../recoil-store/recoil-state';

// Import CSS
import './FilterModal.css';

const label = 'CONTENT.CONFIGURATION-FORM.OBJECT.';
const labelCondition = 'CONTENT.CONFIGURATION-FORM.CONDITION.';

export default function FilterModal({
    action,
    automId,
    automVersionId,
    setOpen,
    conditionInfo,
    variables,
    setOpenVariable,
    handleAdd,
    handleAddNew,
    handleUpdate,
    handleDelete,
    onClose,
    process,
    variableRender,
    automDetail,
    isOk
}) {
    const { t } = useTranslation();
    const [triggerConfig, setTriggerConfig] = useRecoilState(triggerConf);
    const [refresh, setRefresh] = useState({});
    const conditionBackup = useRef();

    const handleClose = () => {
        if (onClose) { onClose(conditionBackup.current); }
        else { setOpen(false); }
    };

    return (
        <Dialog open={true} onClose={handleClose} PaperProps={{ style: { overflowY: "visible" } }}>
            <DialogTitle>
                <div className="modal-title">
                    {handleAdd ? t(label + 'MODAL.HEADER') : t(label + 'MODAL.UPDATE')}
                    <Button data-test="cancelautom-button" onClick={handleClose}>
                        <Close />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent style={{ minWidth: 300, overflowY: "none" }}>
                <ConnectorField automDetail={automDetail} action={action} showFieldPreview={true}
                    triggerConfig={triggerConfig} triggerExpanded={true}
                    debugOpen={false} debugExpand={false}
                    onFlowPage={true}
                    setTriggerConf={setTriggerConfig} automId={automId}
                    automVersionId={automVersionId} rightLocation={false}
                    variables={variables} setOpenVariable={setOpenVariable} resizeClassName="dialog-data-table" />

                <FilterSearch conditionInfo={conditionInfo} variables={variables} setOpenVariable={setOpenVariable}
                    variableRender={variableRender} process={process} conditionBackup={conditionBackup} refresh={refresh} isOk={isOk} />
            </DialogContent>
            <DialogActions className="modal-actions">
                {handleDelete &&
                    <Button
                        onClick={handleDelete}
                        variant="outlined"
                        style={{ float: "left", backgroundColor: "#D75E5E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                        {t('CONTENT.DATATABLE.SELECTEDROWS.DELETE')}
                    </Button>}
                <Button
                    onClick={handleAdd ? handleAdd : handleUpdate}
                    variant="outlined"
                    style={{ float: handleDelete ? "right" : "left", backgroundColor: "#46474E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                    {handleAdd ? t('CONTENT.CONFIGURATION-FORM.SAVE') : t('CONTENT.CONFIGURATION-FORM.UPDATE')}
                </Button>
                {handleAdd && <Button
                    onClick={() => {
                        if (conditionInfo.value?.value === "") {
                            setRefresh({ status: true, value: "" })
                        }
                        handleAddNew()
                    }}
                    variant="outlined"
                    style={{ float: "right", backgroundColor: "#46474E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                    {t('CONTENT.CONFIGURATION-FORM.SAVE-NEW')}
                </Button>}
            </DialogActions>
        </Dialog>
    );
}

export function FilterSearch({ conditionInfo, conditionBackup, variables, variableRender, process, setOpenVariable, setField, refresh, isOk }) {
    const { t } = useTranslation();
    const [operator, setOperator] = useState('eq');

    const options = {
        checkboxes: { label: 'CONTENT.CONFIGURATION-FORM.CONDITION.MODAL.CASE' },
    }

    useEffect(() => {
        if (conditionBackup) {
            conditionBackup.current = JSON.parse(JSON.stringify(conditionInfo));
        }
    }, []);

    useEffect(() => {
        if (conditionInfo?.operator) {
            setOperator(conditionInfo.operator)
        } else {
            setOperator("eq")
        }
    }, [isOk])

    const handleOperatorChange = (e) => {
        let value = e.target.value;
        conditionInfo.operator = value;
        if (setField) {
            setField('operator', value)
        }
        if (value === 'empty' || value === 'nempty') {
            conditionInfo.fields.value.value = '';
        }
        setOperator(value);
    };

    return (
        <div>

            <div className="conf-field-container">
                <FieldLabel name="Key" is_required />
                <TextInput action={conditionInfo} inputName="key" variables={variables} setOpenVariable={setOpenVariable}
                    include={"string"} refresh={refresh.status} outNewValue={{ isOk: true, value: refresh.value }} />
                <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.OBJECT.SEARCH.KEY-EXAMPLE')}</em></small>
            </div>
            {/* </div> */}
            <div className="conf-field-container">
                <span className="dialog-text-medium">Operator: </span>&nbsp;
                <Select value={operator} onChange={handleOperatorChange} variant="outlined">
                    <MenuItem value="eq">= ({t(labelCondition + 'EQ')})</MenuItem>
                    <MenuItem value="ne">&ne; ({t(labelCondition + 'NE')})</MenuItem>
                    <MenuItem value="empty">&empty; ({t(labelCondition + 'EMPTY')}) </MenuItem>
                    <MenuItem value="nempty">{t(labelCondition + 'NEMPTY')}</MenuItem>
                    <MenuItem value="hop">{t(labelCondition + 'HOP')}</MenuItem>
                    <MenuItem value="contains">{t(labelCondition + 'CONTAINS')}</MenuItem>
                </Select>
            </div>
            {["contains", "eq", "ne"].includes(operator) &&
                <>
                    <div className="conf-field-container">
                        <FieldLabel name={t("CONTENT.CONFIGURATION-FORM.VALUE")} is_required />
                        <TextInput
                            action={conditionInfo}
                            inputName="value"
                            variables={variables}
                            setOpenVariable={setOpenVariable}
                            isRequired
                            fieldType="string"
                            displayName={"CONTENT.CONFIGURATION-FORM.VALUE"}
                            hideRequired
                        />
                    </div>
                    < Checkboxes action={conditionInfo} inputName="caseSensitive" options={options.checkboxes} />
                </>
            }
        </div>
    )
}
